import { ChevronLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { Button, RadioGroupButtons } from '@cimplico/components';
import { Subscription, SubscriptionDuration, SubscriptionTier } from '../../../../types/firm';
import { useFirmContext } from '../../../../providers/FirmProvider';
import { useUpdateSubscription } from '../../../../queries/use-update-subscription';
import { toastNotification } from '../../../../components/toast/Toast';
import RadioCards from '../../../../components/radio-cards/RadioCards';

type EditPlanProps = {
    subscription: Subscription;
    onClose: () => void;
};

const tiers = [
    {
        title: 'Basic',
        value: SubscriptionTier.BASIC,
        prices: {
            MONTHLY: 'A$1.00 / entity / month',
            YEARLY: 'A$10.00 / entity / year',
        },
    },
    {
        title: 'Essential',
        value: SubscriptionTier.ESSENTIAL,
        prices: {
            MONTHLY: 'A$1.50 / entity / month',
            YEARLY: 'A$15.00 / entity / year',
        },
    },
    {
        title: 'Professional (Recommended)',
        value: SubscriptionTier.PROFESSIONAL,
        prices: {
            MONTHLY: 'A$2.00 / entity / month',
            YEARLY: 'A$20.00 / entity / year',
        },
    },
];

function EditPlan({ subscription, onClose }: EditPlanProps): JSX.Element {
    const { id } = useFirmContext();
    const [duration, setDuration] = useState<SubscriptionDuration>(subscription.schedule?.duration || subscription.duration);
    const [tier, setTier] = useState<SubscriptionTier>(subscription.schedule?.tier || subscription.tier);

    const updateSubscription = useUpdateSubscription(id);

    function save(): void {
        updateSubscription.mutate(
            { duration, tier },
            {
                onError: () => {
                    toastNotification('error', 'Unable to update the subscription');
                },
                onSuccess: () => {
                    toastNotification('success', 'Successfully updated the subscription');
                    onClose();
                },
            }
        );
    }

    return (
        <div>
            <button className="text-sm text-gray-600 flex items-center gap-1 mb-4" onClick={onClose}>
                <ChevronLeftIcon className="h-4 w-4" />
                Back
            </button>
            <div className="flex items-start justify-between">
                <h3 className="text-xl text-gray-900 font-bold">Manage Plan</h3>
                <Button
                    type="primary"
                    size="sm"
                    label={updateSubscription.isLoading ? 'Updating...' : 'Update Plan'}
                    onClick={save}
                    disabled={updateSubscription.isLoading}
                    isLoading={updateSubscription.isLoading}
                />
            </div>
            <div className="mt-8">
                <p className="text-gray-800 mb-1 text-sm font-medium">Billing Period</p>
                {Boolean(subscription.schedule?.activeEntityAllocation) && (
                    <div className="flex items-center gap-1 mb-2">
                        <ExclamationTriangleIcon className="text-yellow-500 h-5 w-5" />
                        <p className="text-gray-800 text-sm">
                            Please cancel the scheduled update for the active entity allowance before changing billing period.
                        </p>
                    </div>
                )}
                <RadioGroupButtons
                    groupName="plan-billing-period"
                    options={[
                        { label: 'Monthly', value: SubscriptionDuration.MONTHLY },
                        { label: 'Annually', value: SubscriptionDuration.YEARLY },
                    ]}
                    value={duration}
                    onChange={(e): void => setDuration(e.target.value as typeof duration)}
                    disabled={Boolean(subscription.schedule?.activeEntityAllocation)}
                />
                <div className="mt-4">
                    <p className="text-gray-800 mb-1 text-sm font-medium">Tier</p>
                    <RadioCards
                        cards={tiers.map((tier) => {
                            return { title: tier.title, value: tier.value, description: tier.prices[duration] };
                        })}
                        onChange={(e): void => setTier(e as typeof tier)}
                        value={tier}
                    />
                </div>
            </div>
        </div>
    );
}

export default EditPlan;
